import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Server, Plus, Trash2, RefreshCw, ExternalLink, ShieldCheck,
  KeyRound, FlaskConical, FileText, ChevronRight, Info,
  ClipboardCheck, Download, CheckCircle2, XCircle, PlayCircle,
} from "lucide-react";
import { API_URL } from "@/lib/api";

/**
 * POS Vendors — multi-POS test-harness registry (§B of the staging spec).
 *
 * Each row is a data-driven adapter profile. Adding a new vendor is a data
 * change here; no code edits required.
 */
export default function POSVendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ops, setOps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newBaseUrl, setNewBaseUrl] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const [v, o] = await Promise.all([
        api.get("/pos-vendors"),
        api.get("/pos-vendors/operations"),
      ]);
      setVendors(v.data.vendors || []);
      setOps(o.data.operations || []);
    } catch (e) {
      toast.error("Failed to load vendors: " + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  };

  useEffect(() => {
    reload();
  }, []);

  const createVendor = async () => {
    if (!newName.trim()) return toast.error("Vendor name required");
    try {
      await api.post("/pos-vendors", { vendor_name: newName.trim(), base_url: newBaseUrl.trim() });
      toast.success(`Vendor "${newName}" registered`);
      setNewName(""); setNewBaseUrl(""); setCreating(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  const deleteVendor = async (v) => {
    if (!window.confirm(`Delete vendor "${v.vendor_name}"? This is irreversible.`)) return;
    try {
      await api.delete(`/pos-vendors/${v.id}`);
      toast.success("Deleted");
      if (selected?.id === v.id) setSelected(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const saveVendor = async () => {
    if (!editing) return;
    try {
      const patch = {
        base_url: editing.base_url,
        auth_scheme: editing.auth_scheme,
        endpoint_map: editing.endpoint_map,
        field_map: editing.field_map,
        credentials: editing.credentials,
        is_active: editing.is_active,
        is_sandbox: editing.is_sandbox,
        notes: editing.notes,
      };
      const r = await api.put(`/pos-vendors/${editing.id}`, patch);
      toast.success("Vendor updated");
      setSelected(r.data);
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const patchJson = (jsonText, fallback) => {
    try { return JSON.parse(jsonText); } catch { return fallback; }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="pos-vendors-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-white/40">Operations · Multi-POS Harness</div>
          <h1 className="font-display text-3xl text-white flex items-center gap-3 mt-1">
            <Server className="w-6 h-6 kazo-text-champagne" />
            POS Vendor Profiles
          </h1>
          <p className="text-sm text-white/60 mt-2 max-w-3xl">
            Data-driven adapters for every POS vendor we test. Each profile owns the
            base URL, auth scheme, endpoint name-map and per-operation field-map.
            Adding a new vendor is a data change — no code edits required.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/pos-playground"
            className="flex items-center gap-2 px-4 py-2 text-xs rounded-sm border border-amber-300/40 bg-amber-300/10 text-amber-200 hover:bg-amber-300/20 transition"
            data-testid="link-pos-playground"
          >
            <FlaskConical className="w-4 h-4" /> Open Playground
          </Link>
          <button
            onClick={reload}
            className="p-2 rounded-sm border border-white/10 text-white/70 hover:bg-white/10"
            title="Reload"
            data-testid="btn-reload-vendors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setCreating(true)}
            className="flex items-center gap-2 px-4 py-2 text-xs rounded-sm bg-white text-black hover:bg-white/90"
            data-testid="btn-add-vendor"
          >
            <Plus className="w-4 h-4" /> Register Vendor
          </button>
        </div>
      </div>

      {creating && (
        <div className="mb-4 p-4 rounded-sm border border-amber-300/30 bg-amber-300/5 text-white" data-testid="new-vendor-form">
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40">Vendor name</label>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Genesis, Vista, Toshiba TCX, …"
                className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 text-sm rounded-sm"
                data-testid="input-new-vendor-name"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-white/40">Base URL (optional — leave blank for Mock-only)</label>
              <input
                value={newBaseUrl}
                onChange={(e) => setNewBaseUrl(e.target.value)}
                placeholder="https://sandbox.vendor.com/pos"
                className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 text-sm rounded-sm"
                data-testid="input-new-vendor-baseurl"
              />
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setCreating(false)} className="px-3 py-1.5 text-xs text-white/70 hover:text-white" data-testid="btn-cancel-new-vendor">Cancel</button>
            <button onClick={createVendor} className="px-3 py-1.5 text-xs bg-white text-black rounded-sm" data-testid="btn-save-new-vendor">Register</button>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[380px_1fr] gap-4">
        {/* Vendor list */}
        <div className="rounded-sm border border-white/10 bg-black/30" data-testid="vendors-list">
          <div className="p-3 border-b border-white/10 text-[10px] uppercase tracking-widest text-white/40">
            {vendors.length} vendor{vendors.length !== 1 ? "s" : ""}
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {loading ? (
              <div className="p-6 text-white/50 text-sm">Loading…</div>
            ) : vendors.length === 0 ? (
              <div className="p-6 text-white/50 text-sm">No vendors yet.</div>
            ) : vendors.map((v) => (
              <button
                key={v.id}
                onClick={() => { setSelected(v); setEditing(null); }}
                className={`w-full text-left p-3 border-b border-white/5 hover:bg-white/5 flex items-center gap-3 ${
                  selected?.id === v.id ? "bg-white/10" : ""
                }`}
                data-testid={`vendor-row-${v.vendor_name}`}
              >
                <div className={`w-2 h-2 rounded-full ${v.is_active ? "bg-emerald-400" : "bg-white/20"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{v.vendor_name}</div>
                  <div className="text-[11px] text-white/40 truncate">
                    {v.base_url || (v.vendor_name === "Mock POS" ? "built-in simulator" : "no base_url set")}
                  </div>
                </div>
                {v.is_sandbox && (
                  <span className="text-[9px] uppercase tracking-widest text-amber-300 border border-amber-300/40 px-1.5 py-0.5 rounded-sm">sandbox</span>
                )}
                <ChevronRight className="w-4 h-4 text-white/30" />
              </button>
            ))}
          </div>
        </div>

        {/* Detail / editor */}
        <div className="rounded-sm border border-white/10 bg-black/30 min-h-[400px]" data-testid="vendor-detail">
          {!selected ? (
            <div className="p-10 text-center text-white/40">
              <Server className="w-10 h-10 mx-auto mb-3 opacity-40" />
              Select a vendor to view / edit its profile.
            </div>
          ) : editing ? (
            <VendorEditor editing={editing} setEditing={setEditing} onSave={saveVendor} onCancel={() => setEditing(null)} operations={ops} patchJson={patchJson} />
          ) : (
            <VendorDetail vendor={selected} ops={ops} onEdit={() => setEditing(JSON.parse(JSON.stringify(selected)))} onDelete={() => deleteVendor(selected)} />
          )}
        </div>
      </div>
    </div>
  );
}


function VendorDetail({ vendor, ops, onEdit, onDelete }) {
  return (
    <div className="p-5" data-testid="vendor-detail-view">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-display text-2xl text-white flex items-center gap-2">
            {vendor.vendor_name}
            {vendor.is_active ? (
              <span className="text-[10px] uppercase tracking-widest text-emerald-300 border border-emerald-300/40 px-1.5 py-0.5 rounded-sm">active</span>
            ) : (
              <span className="text-[10px] uppercase tracking-widest text-white/40 border border-white/20 px-1.5 py-0.5 rounded-sm">inactive</span>
            )}
          </h2>
          <div className="text-xs text-white/60 mt-1 flex items-center gap-1.5">
            <ExternalLink className="w-3 h-3" /> {vendor.base_url || <span className="italic text-white/40">no base_url</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onEdit} className="px-3 py-1.5 text-xs border border-white/20 text-white hover:bg-white/10 rounded-sm" data-testid="btn-edit-vendor">Edit</button>
          {vendor.vendor_name !== "Mock POS" && (
            <button onClick={onDelete} className="px-3 py-1.5 text-xs border border-red-300/40 text-red-300 hover:bg-red-300/10 rounded-sm flex items-center gap-1" data-testid="btn-delete-vendor">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
        </div>
      </div>
      {vendor.notes && (
        <div className="mt-3 p-3 text-xs text-white/70 bg-white/5 border border-white/10 rounded-sm flex gap-2">
          <Info className="w-4 h-4 shrink-0 text-white/50 mt-0.5" />
          <div className="whitespace-pre-wrap">{vendor.notes}</div>
        </div>
      )}

      <Section title="Auth scheme" icon={<KeyRound className="w-4 h-4" />}>
        <PreJson value={vendor.auth_scheme} />
      </Section>
      <Section title="Credentials (values used to fill {{placeholders}})">
        <PreJson value={vendor.credentials} />
      </Section>
      <Section title="Endpoint map (canonical → vendor path)">
        <PreJson value={vendor.endpoint_map} />
      </Section>
      <Section title="Field maps (canonical → vendor field, per operation)">
        <PreJson value={vendor.field_map} />
      </Section>

      <RunbookPanel vendor={vendor} />

      <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-white/40">
          Created {vendor.created_at?.slice(0, 10)} · {vendor.created_by || "system"}
        </div>
        <Link
          to={`/admin/pos-playground?vendor=${vendor.id}`}
          className="text-xs text-amber-300 hover:underline flex items-center gap-1"
          data-testid="link-open-in-playground"
        >
          <FlaskConical className="w-4 h-4" /> Open in playground →
        </Link>
      </div>
    </div>
  );
}


function VendorEditor({ editing, setEditing, onSave, onCancel, operations, patchJson }) {
  const set = (patch) => setEditing({ ...editing, ...patch });
  const [authText, setAuthText]   = useState(JSON.stringify(editing.auth_scheme || {}, null, 2));
  const [credsText, setCredsText] = useState(JSON.stringify(editing.credentials || {}, null, 2));
  const [epText, setEpText]       = useState(JSON.stringify(editing.endpoint_map || {}, null, 2));
  const [fmapText, setFmapText]   = useState(JSON.stringify(editing.field_map || {}, null, 2));

  const flush = () => {
    set({
      auth_scheme:  patchJson(authText,  editing.auth_scheme),
      credentials:  patchJson(credsText, editing.credentials),
      endpoint_map: patchJson(epText,    editing.endpoint_map),
      field_map:    patchJson(fmapText,  editing.field_map),
    });
  };

  return (
    <div className="p-5" data-testid="vendor-editor">
      <div className="flex items-start justify-between">
        <h2 className="font-display text-2xl text-white">{editing.vendor_name} <span className="text-xs text-white/50">· editing</span></h2>
        <div className="flex items-center gap-2">
          <button onClick={onCancel} className="px-3 py-1.5 text-xs text-white/70 hover:text-white" data-testid="btn-cancel-edit">Cancel</button>
          <button
            onClick={() => { flush(); setTimeout(onSave, 30); }}
            className="px-3 py-1.5 text-xs bg-white text-black rounded-sm"
            data-testid="btn-save-vendor"
          >Save</button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3 mt-4">
        <Field label="Base URL">
          <input value={editing.base_url || ""} onChange={(e) => set({ base_url: e.target.value })} className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm rounded-sm" data-testid="input-base-url"/>
        </Field>
        <Field label="Sandbox / live">
          <div className="flex items-center gap-3 mt-1">
            <label className="text-xs text-white flex items-center gap-1.5"><input type="checkbox" checked={!!editing.is_sandbox} onChange={(e) => set({ is_sandbox: e.target.checked })} data-testid="chk-is-sandbox"/> Sandbox</label>
            <label className="text-xs text-white flex items-center gap-1.5"><input type="checkbox" checked={editing.is_active !== false} onChange={(e) => set({ is_active: e.target.checked })} data-testid="chk-is-active"/> Active</label>
          </div>
        </Field>
      </div>

      <Field label="Notes">
        <textarea value={editing.notes || ""} onChange={(e) => set({ notes: e.target.value })} rows={2} className="w-full bg-black/40 border border-white/10 px-3 py-2 text-xs rounded-sm" data-testid="input-notes" />
      </Field>

      <JsonEditor label="Auth scheme" value={authText} setValue={setAuthText} testid="json-auth" hint='e.g. {"type":"header","headers":{"X-API-Key":"{{api_key}}"}}' />
      <JsonEditor label="Credentials (opaque values used to substitute {{placeholders}})" value={credsText} setValue={setCredsText} testid="json-creds" hint='e.g. {"api_key":"abc123","merchant_id":"M-123"}' />
      <JsonEditor label="Endpoint map (canonical operation → vendor path)" value={epText} setValue={setEpText} testid="json-endpoint-map" />
      <JsonEditor label="Field maps (per operation: canonical → vendor field)" value={fmapText} setValue={setFmapText} testid="json-field-map"
        hint="Every canonical operation key maps to an object of canonical-field ↔ vendor-field pairs. Unmapped canonical fields flow through literally." />
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="mt-5">
      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-1.5">{icon}{title}</div>
      {children}
    </div>
  );
}

function PreJson({ value }) {
  return (
    <pre className="text-[11px] bg-black/50 border border-white/10 rounded-sm p-3 text-white/80 overflow-x-auto whitespace-pre-wrap">
      {JSON.stringify(value ?? {}, null, 2)}
    </pre>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">{label}</label>
      {children}
    </div>
  );
}

function JsonEditor({ label, value, setValue, testid, hint }) {
  const [err, setErr] = useState(null);
  const onChange = (v) => {
    setValue(v);
    try { JSON.parse(v); setErr(null); } catch (e) { setErr(String(e.message)); }
  };
  return (
    <div className="mt-4">
      <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">{label}</label>
      {hint && <div className="text-[11px] text-white/40 mb-1">{hint}</div>}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={Math.min(14, Math.max(6, value.split("\n").length))}
        className={`w-full font-mono text-[11px] bg-black/50 border rounded-sm p-3 text-white/85 ${err ? "border-red-400/50" : "border-white/10"}`}
        data-testid={testid}
      />
      {err && <div className="text-[11px] text-red-300 mt-1">JSON: {err}</div>}
    </div>
  );
}


function RunbookPanel({ vendor }) {
  const [latest, setLatest] = useState(null);
  const [running, setRunning] = useState(false);
  const [mockOnly, setMockOnly] = useState(true);

  const load = async () => {
    try {
      const r = await api.get(`/pos-vendors/${vendor.id}/runbook/latest`);
      setLatest(r.data.exists ? r.data.report : null);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vendor.id]);

  const run = async () => {
    if (!window.confirm(`Run the onboarding runbook for "${vendor.vendor_name}"?\n\nThis will exercise all 10 canonical operations ${mockOnly ? "in Mock mode only" : "against BOTH Mock and the vendor's sandbox"}.`)) return;
    setRunning(true);
    try {
      const r = await api.post(`/pos-vendors/${vendor.id}/runbook/run`, { mock_only: mockOnly });
      setLatest(r.data);
      toast[r.data.overall_pass ? "success" : "warning"](
        r.data.overall_pass ? "All 10 operations passed — vendor certified" : "Runbook complete — review failures below"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Runbook failed");
    }
    setRunning(false);
  };

  return (
    <div className="mt-6 pt-5 border-t border-white/10" data-testid="runbook-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1.5">
          <ClipboardCheck className="w-3.5 h-3.5" /> Onboarding Runbook
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-white flex items-center gap-1.5">
            <input type="checkbox" checked={mockOnly} onChange={(e) => setMockOnly(e.target.checked)} data-testid="chk-runbook-mock" />
            Mock only
          </label>
          <button
            onClick={run}
            disabled={running}
            className="flex items-center gap-2 px-3 py-1.5 text-xs bg-amber-300 text-black rounded-sm font-medium disabled:opacity-50"
            data-testid="btn-run-runbook"
          >
            {running ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
            {running ? "Running…" : "Run Runbook"}
          </button>
          {latest && (
            <a
              href={`${API_URL}/pos-vendors/${vendor.id}/runbook/report.pdf`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 px-3 py-1.5 text-xs border border-white/10 text-white rounded-sm hover:bg-white/10"
              data-testid="btn-runbook-pdf"
            >
              <Download className="w-3.5 h-3.5" /> PDF
            </a>
          )}
        </div>
      </div>

      {!latest ? (
        <div className="text-xs text-white/50 p-3 bg-white/5 border border-white/10 rounded-sm">
          No runbook has been executed for this vendor yet. Run one to produce a signed integration certificate.
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-3 mb-3">
            {latest.overall_pass ? (
              <div className="flex items-center gap-2 text-emerald-300 text-sm">
                <CheckCircle2 className="w-5 h-5" /> Certified · all operations passing
              </div>
            ) : (
              <div className="flex items-center gap-2 text-amber-300 text-sm">
                <XCircle className="w-5 h-5" /> Review needed · some operations failed
              </div>
            )}
            <div className="ml-auto text-[10px] text-white/40">
              {latest.timestamp?.slice(0, 16).replace("T", " ")} · by {latest.run_by}
            </div>
          </div>

          {["mock", "live"].map((phase) => {
            const blob = latest[phase];
            if (!blob) return null;
            return (
              <div key={phase} className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">
                  {phase.toUpperCase()} · {blob.summary.passed}/{blob.summary.total} passed
                </div>
                <div className="grid md:grid-cols-2 gap-1.5">
                  {blob.results.map((r) => (
                    <div key={r.operation}
                         className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-[11px] border ${
                           r.passed ? "border-emerald-400/30 bg-emerald-500/5 text-emerald-100"
                                   : "border-red-400/30 bg-red-500/5 text-red-100"
                         }`}
                         data-testid={`runbook-${phase}-${r.operation}`}>
                      {r.passed ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <XCircle className="w-3.5 h-3.5 text-red-400" />}
                      <span className="flex-1 truncate">{r.label}</span>
                      <span className={r.passed ? "text-emerald-300" : "text-red-300"}>{r.status_code}</span>
                      <span className="text-white/40">{r.latency_ms}ms</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
